import { useEffect, useState, useMemo } from 'react';
import { api } from '../utils/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt   = (n, d = 0) => Number(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtR  = (n)        => '₽ ' + fmt(n, 0);
const fmtUsd = (rub, rate) => rate ? '$ ' + (Number(rub || 0) / rate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : null;
const pct   = (n)        => Number(n || 0).toFixed(1) + '%';

const STATUS_TR = {
  awaiting_packaging:    'Paketlenecek',
  awaiting_registration: 'Kayıt Bekliyor',
  ready_to_ship:         'Kargoya Hazır',
  awaiting_deliver:      'Teslimat Bekliyor',
  delivering:            'Yolda',
  delivered:             'Teslim Edildi',
  cancelled:             'İptal',
};

// ─── Period Selector ──────────────────────────────────────────────────────────

function PeriodTab({ value, active, onClick }) {
  return (
    <button
      onClick={() => onClick(value)}
      style={{
        padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
        background: active ? '#6366f1' : '#f1f5f9',
        color: active ? '#fff' : '#64748b',
        transition: 'all .15s',
      }}
    >
      {value} gün
    </button>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, usd, sub, color = '#6366f1', icon }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '20px 24px',
      boxShadow: '0 1px 4px rgba(0,0,0,.07)', flex: 1, minWidth: 160,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color }}>{value}</div>
      {usd && <div style={{ fontSize: 13, color: '#64748b', marginTop: 3, fontWeight: 500 }}>{usd}</div>}
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── Trend Chart ──────────────────────────────────────────────────────────────

const CHART_H = 120;

function TrendChart({ trend, mode, setMode }) {
  const max = useMemo(() => Math.max(...trend.map(r => Number(r[mode] || 0)), 1), [trend, mode]);

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontWeight: 600, color: '#1e293b' }}>Günlük Trend</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'payout',           label: 'Net Ödeme',     color: '#22c55e' },
            { key: 'customer_payment', label: 'Müşteri Ödemesi', color: '#6366f1' },
            { key: 'commission',       label: 'Komisyon',      color: '#f59e0b' },
          ].map(({ key, label, color }) => (
            <button key={key} onClick={() => setMode(key)} style={{
              padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              background: mode === key ? color : '#f1f5f9',
              color: mode === key ? '#fff' : '#64748b',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {trend.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>Veri yok</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: CHART_H + 20 }}>
          {trend.map((r, i) => {
            const val  = Number(r[mode] || 0);
            const barH = Math.max((val / max) * CHART_H, val > 0 ? 4 : 0);
            const color = mode === 'payout' ? '#22c55e' : mode === 'commission' ? '#f59e0b' : '#6366f1';
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
                title={`${r.label}: ${fmtR(val)}`}>
                <div style={{
                  width: '100%', height: barH, background: color,
                  borderRadius: '4px 4px 0 0', minHeight: val > 0 ? 4 : 0,
                  opacity: 0.85,
                }} />
                {trend.length <= 20 && (
                  <span style={{ fontSize: 9, color: '#94a3b8', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                    {r.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Breakdown Donut ──────────────────────────────────────────────────────────

function BreakdownChart({ kpi }) {
  const customerPmt = Number(kpi?.total_customer_payment || 0);
  const commission  = Number(kpi?.total_commission || 0);
  const payout      = Number(kpi?.total_payout || 0);
  // Henüz settle edilmemiş = payout:0 olan siparişlerin müşteri ödemeleri
  const pending     = Math.max(customerPmt - commission - payout, 0);

  const total = customerPmt || 1;

  const segments = [
    { label: 'Settle Edildi (Sana Ödendi)', value: payout,     color: '#22c55e' },
    { label: 'Ozon Komisyonu',              value: commission,  color: '#f59e0b' },
    { label: 'Henüz Ödenmedi (Bekliyor)',   value: pending,     color: '#cbd5e1' },
  ].filter(s => s.value > 0);

  // SVG pie
  let cumAngle = 0;
  const R = 60, cx = 80, cy = 80;
  const arcs = segments.map(s => {
    const angle = (s.value / total) * 360;
    const start = cumAngle;
    cumAngle += angle;
    const large = angle > 180 ? 1 : 0;
    const toRad = (deg) => (deg - 90) * (Math.PI / 180);
    const x1 = cx + R * Math.cos(toRad(start));
    const y1 = cy + R * Math.sin(toRad(start));
    const x2 = cx + R * Math.cos(toRad(start + angle));
    const y2 = cy + R * Math.sin(toRad(start + angle));
    return { ...s, d: `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z` };
  });

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>Gelir Dağılımı</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        <svg width={160} height={160}>
          {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
          <circle cx={cx} cy={cy} r={38} fill="#fff" />
          <text x={cx} y={cy - 6} textAnchor="middle" fill="#1e293b" fontSize={11} fontWeight={700}>Net</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fill="#22c55e" fontSize={13} fontWeight={700}>
            {customerPmt > 0 ? ((payout / total) * 100).toFixed(0) + '%' : '-'}
          </text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {segments.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{s.label}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{fmtR(s.value)}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{((s.value / total) * 100).toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Product Table ────────────────────────────────────────────────────────────

function ProductTable({ byProduct }) {
  const maxPayout = Math.max(...byProduct.map(r => Number(r.total_payout)), 1);

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>Ürün Bazlı Finansal Özet</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
              {['Ürün', 'Adet', 'Müşteri Ödeme', 'Net Ödeme', 'Komisyon', 'Kom. %', 'İndirim'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Ürün' ? 'left' : 'right', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byProduct.map((r, i) => {
              const barW = Math.max((Number(r.total_payout) / maxPayout) * 100, 2);
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 500, color: '#1e293b', maxWidth: 260 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.offer_id}</div>
                    <div style={{ marginTop: 4, height: 4, background: '#f1f5f9', borderRadius: 2 }}>
                      <div style={{ width: barW + '%', height: 4, background: '#22c55e', borderRadius: 2 }} />
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500 }}>{fmt(r.total_qty)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6366f1' }}>{fmtR(r.total_customer_payment)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#22c55e', fontWeight: 600 }}>{fmtR(r.total_payout)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f59e0b' }}>{fmtR(r.total_commission)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444' }}>{pct(r.avg_commission_pct)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#8b5cf6' }}>{fmtR(r.total_discount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Recent Orders ────────────────────────────────────────────────────────────

function RecentOrders({ orders, rate }) {
  const [open, setOpen] = useState(null);

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>Son Sipariş Detayları</div>
      {orders.length === 0 && (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>Finansal veri bulunan sipariş yok</div>
      )}
      {orders.map((o, i) => {
        const isOpen = open === o.posting_number;
        const netRate = o.customer_payment > 0 ? ((o.payout / o.customer_payment) * 100).toFixed(0) : 0;
        return (
          <div key={i} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 12, marginBottom: 12 }}>
            <div
              onClick={() => setOpen(isOpen ? null : o.posting_number)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            >
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>{o.posting_number}</span>
                <span style={{ marginLeft: 10, fontSize: 11, color: '#94a3b8' }}>{o.order_date}</span>
                <span style={{
                  marginLeft: 10, fontSize: 11, padding: '2px 8px', borderRadius: 10,
                  background: o.status === 'delivered' ? '#dcfce7' : '#f1f5f9',
                  color: o.status === 'delivered' ? '#16a34a' : '#64748b',
                }}>{STATUS_TR[o.status] || o.status}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Müşteri</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#6366f1' }}>{fmtR(o.customer_payment)}</div>
                  {rate && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtUsd(o.customer_payment, rate)}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Net Payout</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>{fmtR(o.payout)}</div>
                  {rate && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtUsd(o.payout, rate)}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Settle oranı</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b' }}>{netRate}%</div>
                </div>
                <span style={{ color: '#cbd5e1', fontSize: 16 }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '3px solid #e2e8f0' }}>
                {(o.items || []).map((item, j) => (
                  <div key={j} style={{
                    background: '#f8fafc', borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                    display: 'grid', gridTemplateColumns: '1fr repeat(5, auto)', gap: '0 20px', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.offer_id} · {item.qty} adet</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Müşteri</div>
                      <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{fmtR(item.customer_price)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Net</div>
                      <div style={{ fontSize: 13, color: '#22c55e', fontWeight: 700 }}>{fmtR(item.payout)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Komisyon</div>
                      <div style={{ fontSize: 13, color: '#f59e0b' }}>{fmtR(Math.abs(item.commission_amount))}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Kom. %</div>
                      <div style={{ fontSize: 13, color: '#ef4444' }}>{pct(item.commission_pct)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>İndirim</div>
                      <div style={{ fontSize: 13, color: '#8b5cf6' }}>{pct(item.discount_pct)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Financials() {
  const [period,    setPeriod]    = useState(30);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [trendMode, setTrendMode] = useState('payout');
  const [rate,      setRate]      = useState(null);   // USD/RUB
  const [rateInfo,  setRateInfo]  = useState('');

  // Kur yükle (bir kez)
  useEffect(() => {
    api.getExchangeRate().then(r => {
      setRate(r.usd_rub);
      const ago = r.updated_at
        ? Math.round((Date.now() - new Date(r.updated_at)) / 60000) + ' dk önce güncellendi'
        : '';
      const label = r.fallback ? 'tahmini kur' : r.stale ? `eski cache · ${ago}` : ago;
      setRateInfo(`1 $ = ₽${Number(r.usd_rub).toFixed(1)} · ${label}`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.getFinancials(period)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [period]);

  const kpi = data?.kpi || {};

  return (
    <div style={{ padding: '24px 32px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b' }}>💰 Finansal Detay</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Ozon komisyon, indirim ve net ödeme analizi
            {rateInfo && (
              <span style={{ marginLeft: 12, fontSize: 12, color: '#22c55e', background: '#f0fdf4', padding: '2px 8px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                📈 {rateInfo}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[30, 90, 180].map(p => <PeriodTab key={p} value={p} active={period === p} onClick={setPeriod} />)}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 10, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: '#94a3b8' }}>
          Yükleniyor…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Bilgi notu */}
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#1d4ed8', lineHeight: 1.7 }}>
            <strong>⚠️ Bu rakamlar kar değildir.</strong> "Settle Edildi" = Ozon'un sana aktardığı tutar (sadece teslim edilmiş ve iade süresi geçmiş siparişler).
            Gerçek kârı görmek için <strong>Karlılık</strong> ekranına gir — orada mal maliyetini düşerek net kârı hesaplıyoruz.
            Müşteri ödemelerinin büyük kısmı "Henüz Ödenmedi" sütununda görünür — bu kesinti değil, bekleyen siparişlerin payoutudur.
          </div>

          {/* KPI Row */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <KpiCard icon="🛒" label="Sipariş Sayısı"
              value={fmt(kpi.order_count)} color="#6366f1" />
            <KpiCard icon="💳" label="Müşteri Ödemeleri"
              value={fmtR(kpi.total_customer_payment)}
              usd={fmtUsd(kpi.total_customer_payment, rate)}
              color="#6366f1"
              sub="Müşterinin Ozon'a ödediği toplam" />
            <KpiCard icon="✅" label="Settle Edildi (Ödenen)"
              value={fmtR(kpi.total_payout)}
              usd={fmtUsd(kpi.total_payout, rate)}
              color="#22c55e"
              sub="Ozon'un sana aktardığı (teslim+iade süresi geçmiş)" />
            <KpiCard icon="🏷️" label="Ozon Komisyonu"
              value={fmtR(kpi.total_commission)}
              usd={fmtUsd(kpi.total_commission, rate)}
              color="#f59e0b"
              sub={`Ort. oran: ${pct(kpi.avg_commission_pct)}`} />
            <KpiCard icon="⏳" label="Bekleyen Ödeme"
              value={fmtR(Math.max((kpi.total_customer_payment||0) - (kpi.total_commission||0) - (kpi.total_payout||0), 0))}
              usd={fmtUsd(Math.max((kpi.total_customer_payment||0) - (kpi.total_commission||0) - (kpi.total_payout||0), 0), rate)}
              color="#94a3b8"
              sub="Henüz settle edilmemiş siparişler" />
          </div>

          {/* Trend + Breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20 }}>
            <TrendChart trend={data?.trend || []} mode={trendMode} setMode={setTrendMode} />
            <BreakdownChart kpi={kpi} />
          </div>

          {/* Product Table */}
          {(data?.byProduct || []).length > 0 && (
            <ProductTable byProduct={data.byProduct} />
          )}

          {/* Recent Orders */}
          <RecentOrders orders={data?.recentOrders || []} rate={rate} />

        </div>
      )}
    </div>
  );
}
